<template>

<!--
	The result-sync prompt: opened by the `sync-open` bus event (after sign-in
	with a pre-computed gap, or manually with `force` so it computes its own gap
	with a loading state). A quiet bottom-sheet like the sign-in dialog. Syncing
	is optional: the scrim and "Not now" both dismiss it. Live plays show their
	score; practice plays are tagged.
-->

<div class="sync" v-on:click.self="dismiss">

	<div class="sync-scrim" v-on:click="dismiss"></div>

	<div class="sync-sheet" role="dialog" aria-modal="true" aria-label="Save your results">

		<app-mark :size="34" class="sync-mark" />

		<!-- success state -->
		<template v-if="done">

			<h2 class="sync-head">Synced</h2>
			<p class="sync-sub">{{ savedCount }} play{{ savedCount === 1 ? '' : 's' }} {{ savedCount === 1 ? 'is' : 'are' }} now on your account.</p>

		</template>

		<!-- still working out what's on the device vs the account -->
		<template v-else-if="gapLoading">

			<h2 class="sync-head">Sync your plays</h2>
			<div class="sync-loading"><app-loader /></div>

		</template>

		<!-- nothing on this device the account doesn't already hold -->
		<template v-else-if="!count">

			<h2 class="sync-head">All synced</h2>
			<p class="sync-sub">Every play on this device is already on your account.</p>

			<div class="sync-actions">
				<button type="button" class="sync-skip" v-on:click="dismiss">Close</button>
			</div>

		</template>

		<!-- the gap, ready to sync -->
		<template v-else>

			<h2 class="sync-head">Sync your plays</h2>
			<p class="sync-sub">We found {{ count }} play{{ count === 1 ? '' : 's' }} on this device that {{ count === 1 ? "isn't" : "aren't" }} on your account yet. Sync {{ count === 1 ? 'it' : 'them' }} to keep your record across devices.</p>

			<ul class="sync-list">
				<li v-for="play in displayPlays" :key="play.date" class="sync-list-row">
					<span class="sync-list-date">{{ label(play.date) }}</span>
					<span v-if="play.mode === 'practice'" class="sync-tag">Practice</span>
					<span v-else-if="play.score != null" class="sync-list-score">{{ play.score }}</span>
				</li>
			</ul>

			<div class="sync-actions">
				<app-btn kind="primary" block :loading="busy" v-on:click="confirm">Sync {{ count === 1 ? 'play' : 'plays' }}</app-btn>
				<button type="button" class="sync-skip" v-on:click="dismiss">Not now</button>
			</div>

		</template>

	</div>

</div>

</template>

<script>

/*
	Hosted once at the app root (App.vue), opened by the `sync-open` bus event.
	It opens IMMEDIATELY: when handed a pre-computed `plays` gap (the on-sign-in
	path) it shows it at once; when opened with `force` (a manual sync) it shows a
	loading state while it computes the gap itself (game/syncGap does the
	result/dates round trip), so there's no pause before the dialog appears. On
	confirm it dispatches game/sync, then a brief "Synced" state, then closes.
*/

export default {

	name: 'SyncPrompt',

	props: {
		// a pre-computed gap [{ date, picks, mode, score }] (sign-in path), or null
		// to compute it here (manual open)
		plays: {
			default: null
		},
		// manual open: re-offer plays even if a prior sync flagged them claimed
		force: {
			type: Boolean,
			default: false
		}
	},

	data: function() {

		return {
			// posting to the server
			busy: false,
			// flipped to the brief "Synced" confirmation before auto-close
			done: false,
			// how many the server actually took (synced + already-present)
			savedCount: 0,
			// computing the gap (manual open) — shows the loading state
			gapLoading: false,
			// the gap computed here when no `plays` prop was passed
			resolved: null
		}

	},

	created: function() {

		// a pre-computed gap was passed (sign-in path): show it at once. Otherwise
		// compute it here, showing a loading state during the result/dates call.
		if (this.plays) return

		var self = this

		this.gapLoading = true

		this.$store.dispatch('game/syncGap', { force: this.force }).then(function(gap) {

			self.resolved = gap || []
			self.gapLoading = false

		})

	},

	computed: {

		// the gap to render: the pre-computed prop, else what we resolved
		displayPlays: function() {

			return this.plays || this.resolved || []

		},

		count: function() {

			return this.displayPlays.length

		}

	},

	methods: {

		// 'YYYY-MM-DD' -> a short, readable label (e.g. "23 Jun 2026")
		label: function(date) {

			var parts = String(date).split('-')

			if (parts.length !== 3) return date

			var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
			var month = months[parseInt(parts[1], 10) - 1] || parts[1]

			return parseInt(parts[2], 10) + ' ' + month + ' ' + parts[0]

		},

		confirm: function() {

			if (this.busy) return

			var self = this

			this.busy = true

			this.$store.dispatch('game/sync', this.displayPlays).then(function(json) {

				self.busy = false

				if (!json || json.error) {

					// the api layer already raised a global error toast; just close
					self.$emit('close')
					return

				}

				var synced = (json.synced || []).length
				var skipped = (json.skipped || []).length

				self.savedCount = synced + skipped
				self.done = true

				// let the confirmation read for a beat, then close
				setTimeout(function() { self.$emit('close') }, 1300)

			})

		},

		dismiss: function() {

			if (this.busy) return

			this.$emit('close')

		}

	}

}

</script>

<style scoped>

.sync {
	position: fixed;
	inset: 0;
	z-index: 110;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
}

.sync-scrim {
	position: absolute;
	inset: 0;
	background: rgba(28, 26, 22, 0.32);
}

.sync-sheet {
	position: relative;
	margin: 0 18px 18px;
	background: var(--paper);
	border: 1px solid var(--hairline);
	border-radius: 14px;
	padding: 26px 24px;
	box-shadow: 0 20px 60px rgba(40, 34, 24, 0.22);
	animation: sync-settle 0.35s ease both;
}

@keyframes sync-settle {
	from { transform: translateY(16px); opacity: 0; }
	to { transform: translateY(0); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
	.sync-sheet { animation: none; }
}

.sync-mark {
	display: block;
	margin-bottom: 16px;
}

.sync-head {
	font-family: var(--font-display);
	font-size: 24px;
	font-weight: 500;
	letter-spacing: -0.01em;
	margin: 0;
	color: var(--ink);
}

.sync-sub {
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--muted-2);
	line-height: 1.45;
	margin: 8px 0 18px;
}

.sync-loading {
	display: flex;
	justify-content: center;
	padding: 18px 0 8px;
}

.sync-list {
	list-style: none;
	margin: 0 0 22px;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 6px;
	max-height: 168px;
	overflow-y: auto;
}

.sync-list-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	padding: 9px 12px;
	border: 1px solid var(--hairline);
	border-radius: 8px;
	background: #fbf8f1;
}

.sync-list-date {
	font-family: var(--font-mono);
	font-size: 12px;
	letter-spacing: 0.04em;
	color: var(--ink);
}

/* the live play's score, right-aligned */
.sync-list-score {
	font-family: var(--font-mono);
	font-size: 12px;
	font-weight: 500;
	color: var(--muted-2);
}

/* the practice tag: a small pill, right-aligned */
.sync-tag {
	flex: none;
	padding: 2px 8px;
	border: 1px solid var(--tile);
	border-radius: 999px;
	font-family: var(--font-mono);
	font-size: 9px;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: var(--muted-2);
	background: var(--paper);
}

.sync-actions {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.sync-skip {
	background: none;
	border: none;
	padding: 4px 0 0;
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--muted-2);
	cursor: pointer;
	align-self: center;
}

.sync-skip:hover {
	color: var(--ink);
}

/* desktop: lift off the bottom edge and centre as a capped card (matches the
   sign-in dialog), rather than stretching nearly full width */
@media (min-width: 600px) {

	.sync {
		justify-content: center;
		align-items: center;
	}

	.sync-sheet {
		margin: 0;
		width: 100%;
		max-width: 400px;
	}

}

</style>
